/* Who to contact about a problem (designs/problem-contacts.md): the
   "Something wrong?" block on About Hermes (PC7) and the Admin's edit card
   on Permissions (PC6). Both read GET /api/problem-contacts — the same
   value the intake guide serves Friday — so the page and the assistant
   cannot disagree. A failed read names itself; an empty list is not
   recorded, never "nobody" by default. */
import { useEffect, useState, type JSX } from "react";
import type { UserRef } from "../../../shared/organizer-roles";
import type { ServedProblemContacts } from "../../../shared/problem-contacts";
import { asJson } from "../intake/api";
import { useAppState } from "../shell/store";
import { PersonPicker } from "../intake/PersonPicker";

/* the shape is checked, not assumed: this block sits on the landing page
   (shared-home.md SH2), so an unexpected answer must read as a named error,
   never crash the first screen a person sees */
const isServed = (v: unknown): v is ServedProblemContacts =>
  typeof v === "object" &&
  v !== null &&
  Array.isArray((v as ServedProblemContacts).people) &&
  typeof (v as ServedProblemContacts).provenance === "object" &&
  (v as ServedProblemContacts).provenance !== null;

const checked = (v: unknown): ServedProblemContacts => {
  if (!isServed(v)) throw new Error("the contact list came back in an unexpected shape");
  return v;
};

const getContacts = async (): Promise<ServedProblemContacts> =>
  checked(await asJson(await fetch("/api/problem-contacts")));

const putContacts = async (people: UserRef[]): Promise<ServedProblemContacts> =>
  checked(
    await asJson(
      await fetch("/api/problem-contacts", {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ people }),
      }),
    ),
  );

function useContacts(): {
  served: ServedProblemContacts | null;
  error: string | null;
  setServed: (s: ServedProblemContacts) => void;
} {
  const [served, setServed] = useState<ServedProblemContacts | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    getContacts()
      .then(setServed)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);
  return { served, error, setServed };
}

/* the provenance line, in the same words the guide serves */
function Provenance({ served }: { served: ServedProblemContacts }): JSX.Element {
  return served.provenance.state === "confirmed" ? (
    <span className="stamp">{served.provenance.basis}</span>
  ) : (
    <span className="not-recorded">
      Not recorded — {served.provenance.known} Who can name one: {served.provenance.whoCanConfirm}.
    </span>
  );
}

/* About Hermes (PC7) */
export function ProblemContactsBlock(): JSX.Element {
  const { served, error } = useContacts();
  const { store } = useAppState();
  return (
    <section className="about-block" data-problem-contacts>
      <h3>Something wrong?</h3>
      {error !== null && <p className="capture-error">{error}</p>}
      {served !== null && (
        <>
          {served.people.length > 0 && (
            <p>
              For a problem with Hermes itself — something broken, wrong or missing —{" "}
              {store.on("issues") ? (
                <button className="btn link" onClick={() => store.openReport()}>
                  submit an issue
                </button>
              ) : (
                "submit an issue"
              )}{" "}
              first. Contact <b>{served.people.map((p) => p.name).join(" or ")}</b> for a permission you
              need, for something urgent, or if submitting an issue doesn't work.
            </p>
          )}
          <p>
            <Provenance served={served} />
          </p>
          <p>
            For a question about one effort, ask its <b>point of contact</b> — it is on every effort's
            record.
          </p>
        </>
      )}
    </section>
  );
}

/* Permissions (PC6) — Admin-only, because the page is. The server refuses a
   non-Admin save regardless (PC5); a refusal is shown in its own words. */
export function ProblemContactsCard(): JSX.Element {
  const { served, error, setServed } = useContacts();
  const [draft, setDraft] = useState<UserRef[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const people = draft ?? served?.people ?? [];
  const changed = draft !== null;

  const remove = (userId: string): void => {
    setSaved(false);
    setDraft(people.filter((p) => p.userId !== userId));
  };
  const add = (p: UserRef | null): void => {
    if (p === null || people.some((q) => q.userId === p.userId)) return;
    setSaved(false);
    setDraft([...people, p]);
  };
  const save = async (): Promise<void> => {
    setSaving(true);
    setSaveError(null);
    try {
      setServed(await putContacts(people));
      setDraft(null);
      setSaved(true);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="card confirmed" data-problem-contacts-edit>
      <div className="card-head">
        <h3 className="title">Who to contact about problems</h3>
        {served !== null && <Provenance served={served} />}
      </div>
      <p className="slot-note">
        Shown on About Hermes and served to Claude through the intake guide. Questions about one
        effort go to that effort's point of contact instead.
      </p>
      {error !== null && <p className="capture-error">{error}</p>}
      {served !== null && (
        <>
          <ul className="slots">
            {people.length === 0 ? (
              <li>
                <span className="slot-main">
                  <div className="slot-note not-recorded">Not recorded — nobody is on the list.</div>
                </span>
              </li>
            ) : (
              people.map((p) => (
                <li key={p.userId}>
                  <span className="slot-main">{p.name}</span>
                  <button
                    className="btn ghost small"
                    aria-label={`Remove ${p.name}`}
                    onClick={() => remove(p.userId)}
                  >
                    Remove
                  </button>
                </li>
              ))
            )}
          </ul>
          <PersonPicker label="Add a contact" value={null} onChange={add} />
          <div className="form-actions">
            <button className="btn small" disabled={!changed || saving} onClick={() => void save()}>
              Save contacts
            </button>
            {saved && <span className="stamp">Saved.</span>}
          </div>
          {saveError !== null && <p className="capture-error">{saveError}</p>}
        </>
      )}
    </section>
  );
}
